import { useEffect } from 'react'
import { FieldPath, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Alert, Loader, Stack, Text } from '@mantine/core'
import { useDidUpdate } from '@mantine/hooks'
import * as Sentry from '@sentry/react'
import * as yup from 'yup'

import { DefaultReviewForm } from './DefaultReviewForm'
import { EventCancelledReviewForm } from './EventCancelledReviewForm'

import { useNewScriptTraining } from '@/components/Event/hooks/useNewScriptTraining'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { LocationForm } from '@/components/Event/NewScript/components/common/forms/LocationForm'
import {
  ActionText,
  ActionTitle,
  ContinueButton,
} from '@/components/Event/NewScript/components/common/ScriptTimeline'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ScriptTimeline } from '@/components/Event/NewScript/components/common/ScriptTimeline/ScriptTimeline'
import { TimelineAction } from '@/components/Event/NewScript/components/common/ScriptTimeline/TimelineAction'
import { EventCompletedCard } from '@/components/Event/NewScript/components/Review/EventCompletedCard'
import { LowConfidenceWordsFieldArray } from '@/components/Event/NewScript/components/Review/LowConfidenceWordsFieldArray'
import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import {
  EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS,
  EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED,
  EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED,
} from '@/components/Event/NewScript/schemas'
import { endStepSchema } from '@/components/Event/NewScript/schemas/reviewStepSchema'
import { getIdentityDocumentExemptAttendeeIds } from '@/components/Event/NewScript/utils/attendees'
import { LowConfidenceWordStatus, ScriptStep } from '@/constants'
import { useAuth } from '@/hooks/useAuth'
import { useLowConfidenceWordsQuery } from '@/queries/events'
import { Event, LowConfidenceWord, ScriptAttendee, ScriptData } from '@/types'

type EndStepType = yup.InferType<typeof endStepSchema>

type DefaultReviewFormValues = {
  state: string
  county: string
  lowConfidenceWords: LowConfidenceWord[] | null
  events:
    | {
        timestamps: string | null
        spellings: string | null
        notes: string | null
        attendees: ScriptAttendee[]
      }[]
    | null
}

export type ReviewStepProps = {
  event: Event
  refetchEvent?: () => Promise<unknown>
}

export const ReviewStep = ({ event, refetchEvent }: ReviewStepProps) => {
  const { user } = useAuth()
  const { completeAction, canContinue, isActionCompleted } = useStepActions(
    ScriptStep.REVIEW,
    event,
  )
  const { state, actions, submitScript, isSubmittingScript } = useScriptState()
  const scriptData = state as ScriptData
  const identityDocumentExemptAttendeeIds = scriptData.events.flatMap(
    eventState => getIdentityDocumentExemptAttendeeIds(eventState.attendees),
  )
  const { hasTrainingAccess, resetScript, isResetting } =
    useNewScriptTraining(event)
  const { data: lowConfidenceAsrWords, error: lowConfidenceAsrWordsError } =
    useLowConfidenceWordsQuery(event, {
      enabled:
        scriptData.events[0].low_confidence_words === null &&
        !hasTrainingAccess,
      cacheTime: Infinity,
    })

  const methods = useForm<DefaultReviewFormValues>({
    mode: 'all',
    resolver: yupResolver(endStepSchema),
    context: {
      [EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED]: true,
      [EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS]:
        identityDocumentExemptAttendeeIds,
      [EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED]: true,
    },
    defaultValues: {
      state: scriptData.reporter_state ?? undefined,
      county: scriptData.reporter_county ?? undefined,
      lowConfidenceWords: scriptData.events[0].low_confidence_words ?? [],
      events: scriptData.events.map(state => ({
        timestamps: state.timestamps,
        spellings: state.spellings,
        notes: state.notes,
        attendees: state.attendees,
      })),
    },
  })
  const lowConfidenceWords = methods.watch('lowConfidenceWords')

  useEffect(() => {
    const lcwValue = methods.getValues('lowConfidenceWords')
    if (hasTrainingAccess && lcwValue === null) {
      methods.setValue('lowConfidenceWords', [])
    }
  }, [hasTrainingAccess, methods])

  useDidUpdate(() => {
    Sentry.captureException(
      new Error(
        `Couldn't load low confidence words ${JSON.stringify(
          lowConfidenceAsrWordsError,
        )}`,
      ),
    )
    methods.setValue('lowConfidenceWords', [])
  }, [lowConfidenceAsrWordsError])

  useDidUpdate(() => {
    const lowConfidenceWords = (lowConfidenceAsrWords ?? []).map(word => ({
      ...word,
      status: LowConfidenceWordStatus.UNRESOLVED,
      correct_text: word.word,
    }))
    methods.setValue('lowConfidenceWords', lowConfidenceWords)
  }, [lowConfidenceAsrWords])

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name && ['county', 'state'].includes(name)) {
        return actions.updateGlobalState({
          reporter_state: value.state,
          reporter_county: value.county,
        })
      }

      if (name && name.includes('lowConfidenceWords')) {
        return actions.updateGlobalState({
          events: (state as ScriptData).events.map(event => ({
            ...event,
            low_confidence_words: value.lowConfidenceWords,
          })),
        })
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, actions])

  const validateAndCompleteAction = async (
    validationKeys: FieldPath<EndStepType>[],
    actionKey: string,
  ) => {
    const results = await Promise.all(
      validationKeys.map(key => methods.trigger(key)),
    )

    if (results.every(isValid => isValid)) {
      completeAction(actionKey)
    }
  }

  return (
    <FormProvider {...methods}>
      <Stack align="flex-start" spacing="xl">
        <ScriptTimeline
          title={
            scriptData.events.length > 1
              ? `${scriptData.events.length + 2}. Review`
              : '6. Review'
          }
        >
          {scriptData.events.map((eventState, witnessIndex) =>
            eventState.first_skipped_step ? (
              <EventCancelledReviewForm
                key={witnessIndex}
                event={event}
                state={eventState}
                witnessIndex={witnessIndex}
              />
            ) : (
              <DefaultReviewForm
                key={witnessIndex}
                event={event}
                state={eventState}
                witnessIndex={witnessIndex}
              />
            ),
          )}
          {/* Low confidence words */}
          <TimelineAction
            actionKey="lowConfidenceWords"
            title="Correct detected words with low confidence"
            onComplete={() =>
              validateAndCompleteAction(
                ['lowConfidenceWords'],
                'low_confidence_words',
              )
            }
            completed={isActionCompleted('low_confidence_words')}
          >
            <ActionContainer outer>
              <Stack>
                {!lowConfidenceWords ? (
                  <Loader />
                ) : lowConfidenceWords.length === 0 ? (
                  <ActionContainer>
                    There are no low confidence words, no action needed.
                  </ActionContainer>
                ) : (
                  <Stack>
                    <ActionContainer>
                      <LowConfidenceWordsFieldArray
                        name="lowConfidenceWords"
                        event={event}
                      />
                    </ActionContainer>
                    <ActionContainer>
                      <ActionTitle>Reminder</ActionTitle>
                      <ActionText>
                        Review the spelling of all words marked with the cross
                        in the transcript. Correct any inaccuracies by rewriting
                        the word in the input to the proper form and clicking
                        "correct" or click "unclear" if you are unsure.
                      </ActionText>
                    </ActionContainer>
                  </Stack>
                )}
              </Stack>
            </ActionContainer>
          </TimelineAction>
          {/* Location */}
          <TimelineAction
            actionKey="location"
            title="Declare your location during proceeding"
            onComplete={() =>
              validateAndCompleteAction(['state', 'county'], 'location')
            }
            completed={isActionCompleted('location')}
          >
            <ActionContainer outer>
              <ActionContainer>
                <LocationForm />
              </ActionContainer>
            </ActionContainer>
          </TimelineAction>
          <ContinueButton
            event={event}
            canContinue={
              canContinue() &&
              (event.is_waiting_for_reporter_feedback || hasTrainingAccess)
            }
            loading={isSubmittingScript || isResetting}
            handleClick={async () => {
              await methods.trigger()

              if (!hasTrainingAccess) {
                await submitScript(state as ScriptData)
                await refetchEvent?.()
              } else {
                resetScript()
              }
            }}
            label="Finish proceeding"
            step={ScriptStep.REVIEW}
          />
        </ScriptTimeline>
        {methods.formState.errors && (
          <FormErrors errors={methods.formState.errors} />
        )}
        {hasTrainingAccess && (
          <Alert title="Keep in mind" color="red">
            <Text>
              Please note, since this is a demo event the form will not be
              actually submitted. Instead the script will start over so you can
              try different scenarios, such as event cancellation process, etc.
            </Text>
          </Alert>
        )}
        {user?.role === 'admin' && !event.is_waiting_for_reporter_feedback && (
          <EventCompletedCard event={event} />
        )}
      </Stack>
    </FormProvider>
  )
}
